import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import proxyapi from '../services/ProxyApi';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    // Extract the session_id from the URL query parameters
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('Missing session information');
      setIsLoading(false);
      return;
    }

    const simulateWebhook = async (sessionId) => {
      try {
        setIsSimulating(true);
        
        // First check if payment already exists
        const verifyResponse = await proxyapi.get(`/api/v1/payments/verify?session_id=${sessionId}`);
        if (verifyResponse.data.status === 'success') {
          // Payment already processed, no need to simulate
          console.log('Payment already processed, skipping simulation');
          setIsSimulating(false);
          return verifyResponse.data;
        }
        
        // Only simulate if not already processed
        await proxyapi.post('/api/v1/test/simulate-webhook', { session_id: sessionId });
        setIsSimulating(false);
        return null;
      } catch (err) {
        console.error('Error simulating webhook:', err);
        setIsSimulating(false);
        return null;
      }
    };
    

    const verifyPayment = async () => {
      try {
        // In development, simulate the webhook first
        let preloadedData = null;
        if (process.env.NODE_ENV === 'development') {
          preloadedData = await simulateWebhook(sessionId);
        }
    
        // If we already have the data from simulation, use it
        if (preloadedData) {
          setPaymentDetails(preloadedData);
        } else {
          // Otherwise verify the payment
          const response = await proxyapi.get(`/api/v1/payments/verify?session_id=${sessionId}`);
          setPaymentDetails(response.data);
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('Could not verify payment. Please contact support.');
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [location.search]);

  const handleContinue = () => {
    navigate('/projects');
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
        {isLoading || isSimulating ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
            <p className="text-gray-600">
              {isSimulating ? 'Processing your payment...' : 'Verifying your payment...'}
            </p>
          </div>
        ) : error ? (
          <div>
            <svg
              className="w-16 h-16 text-red-500 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <h1 className="text-2xl font-bold mb-4">Payment Verification Failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleContinue}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
            >
              Return to Dashboard
            </button>
          </div>
        ) : (
          <div>
            <svg
              className="w-16 h-16 text-green-500 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <h1 className="text-2xl font-bold mb-4">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">Thank you for your purchase.</p>

            {paymentDetails && (
              <div className="text-left bg-gray-50 p-4 rounded-md mb-6">
                <p className="mb-2">
                  <span className="font-semibold">Amount:</span> ${paymentDetails.amount}
                </p>
                <p className="mb-2">
                  <span className="font-semibold">Plan:</span> {paymentDetails.title}
                </p>
                <p>
                  <span className="font-semibold">Valid until:</span>{' '}
                  {formatDate(paymentDetails.expiresAt)}
                </p>
              </div>
            )}

            <button
              onClick={handleContinue}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
            >
              Continue to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;